<template>
  <div class="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-100 py-8 px-4">
    <div class="max-w-md mx-auto">
      <div class="bg-white rounded-2xl shadow-xl p-8">
        <!-- Header -->
        <div class="text-center mb-8">
          <div class="inline-flex items-center justify-center w-16 h-16 bg-sky-800 rounded-full mb-4">
            <i class="fas fa-lock text-white text-2xl"></i>
          </div>
          <h1 class="text-3xl font-bold text-gray-800 mb-2">Admin Login</h1>
          <p class="text-gray-600">Enter your admin credentials</p>
        </div>

        <!-- Login Form -->
        <form @submit.prevent="handleLogin" class="space-y-6">
          <!-- Password Field -->
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
              Admin Password
            </label>
            <div class="relative">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-800 focus:border-transparent outline-none transition-all duration-200"
                placeholder="Enter admin password"
                required
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex items-center">
              <i class="fas fa-exclamation-triangle text-red-500 mr-3"></i>
              <span class="text-red-800">{{ error }}</span>
            </div>
          </div>

          <!-- Login Button -->
          <button
            type="submit"
            :disabled="loading"
            class="w-full bg-sky-800 hover:bg-sky-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <i v-if="loading" class="fas fa-spinner animate-spin"></i>
            <i v-else class="fas fa-sign-in-alt"></i>
            <span>{{ loading ? 'Signing in...' : 'Sign In' }}</span>
          </button>
        </form>

        <!-- Footer -->
        <div class="mt-8 text-center">
          <p class="text-xs text-gray-500">
            Secure admin access for QR Check-In management
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter } from 'vue-router'

export default {
  name: 'LoginView',
  setup() {
    const router = useRouter()
    const password = ref('')
    const showPassword = ref(false)
    const loading = ref(false)
    const error = ref('')

    // Hashed admin password using SHA-256 with salt
    // Original: FVV!Admin
    const ADMIN_PASSWORD_HASH = 'd0b3ad1fb0933a27b64e1efdf8fa93b05049c1d9fbb054958a7725d83386b9be'

    // Hash function using Web Crypto API
    const hashPassword = async (password) => {
      const encoder = new TextEncoder()
      const data = encoder.encode(password + 'salt') // Add salt for security
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    }

    const handleLogin = async () => {
      try {
        loading.value = true
        error.value = ''

        // Simulate loading delay for better UX
        await new Promise(resolve => setTimeout(resolve, 1000))

        // Hash the entered password and compare
        const hashedInput = await hashPassword(password.value)

        if (hashedInput === ADMIN_PASSWORD_HASH) {
          // Store authentication state
          localStorage.setItem('isAdminAuthenticated', 'true')
          localStorage.setItem('loginTimestamp', Date.now().toString())
          
          // Redirect to admin dashboard
          router.push('/')
        } else {
          error.value = 'Invalid password. Please try again.'
          password.value = ''
        }
      } catch (err) {
        console.error('Login error:', err)
        error.value = 'An error occurred during login. Please try again.'
      } finally {
        loading.value = false
      }
    }

    return {
      password,
      showPassword,
      loading,
      error,
      handleLogin
    }
  }
}
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
